import React, { useEffect, useState } from 'react'
import { Alert, FormControlLabel, Radio, RadioGroup, Stack, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import OperationComparisonView from './OperationComparisonView'
import { getOperationPublishPreview } from './operationLibraryApi'

/**
 * PublishOperationDialog (C4, §8.7) — publicar a la biblioteca el job de un nodo embebido
 * (CA4.1/CA4.2). El código se TECLEA a mano: no hay propuesta automática ni derivación del nombre
 * del nodo (S73, D11). Nombre y descripción son opcionales.
 *
 * Al teclear el código se previsualiza con `GET .../operationPublishPreview?code=` (N16, S78/S79):
 * - `codeExists` false ⇒ se creará la operación con su versión 1, sin avisos ni opciones;
 * - `codeExists` true ⇒ aviso INFORMATIVO K37 —que no es el error E-OPL-02— la comparación contra la
 *   versión vigente, y una elección EXPLÍCITA entre K38 (versión n+1) y K39 (otro código). No hay
 *   tercera vía ni comportamiento por defecto.
 *
 * Publicar es un cambio PENDIENTE del editor (Δ9): lo escribe el guardado general del workflow, y
 * por eso un revert lo deja sin efecto (S84).
 *
 * Historia 152 (S44, CU-12 · RN-01.24) — **`lockedCode`**: PROPONER a la librería desde un nodo
 * `CUSTOMIZED` es el mismo acto `PUBLISH`, pero con el código YA DECIDIDO: es forzosamente el de su
 * operación vinculada y no se teclea. Con `lockedCode`:
 * - desaparece el campo del código y con él la elección K38/K39 — no hay "otro código" posible;
 * - se anuncia "This will create version {{version}} of {{code}}" (J28) con la versión que devuelve
 *   N16 (`currentVersionNumber` + 1), que es UNA clave con interpolación, no trozos concatenados;
 * - `asNewVersionOfCode` sale SIEMPRE con ese código, que es lo que el contrato exige para que Sun
 *   no lo rechace con `E-LNK-05` (§8.3).
 * Sin `lockedCode` el diálogo es exactamente el de #130 sobre un nodo embebido (S73), sin cambio.
 */
const PublishOperationDialog = ({ open = false, workflowId = 0, nodeId = '', lockedCode = '', onPublish = () => {}, onClose = () => {} }) => {
  const { t_ } = useSmartTranslation()
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [preview, setPreview] = useState({})
  const [choice, setChoice] = useState('')

  useEffect(() => {
    if (!open) {
      setCode('')
      setName('')
      setDescription('')
      setPreview({})
      setChoice('')
      return
    }
    // S44: con el código decidido no hay nada que teclear — se parte ya de él.
    if (lockedCode) setCode(lockedCode)
  }, [open, lockedCode])

  useEffect(() => {
    let current = true
    if (!open || !code || !workflowId || !nodeId) {
      setPreview({})
      return () => { current = false }
    }
    getOperationPublishPreview(workflowId, nodeId, code)
      .then((result) => { if (current) setPreview(result || {}) })
      // Una previsualización que no se puede leer no bloquea el diálogo: simplemente no anticipa
      // nada, y el servidor sigue siendo quien decide al guardar.
      .catch(() => { if (current) setPreview({}) })
    return () => { current = false }
  }, [open, code, workflowId, nodeId])

  const codeExists = preview.codeExists === true

  const publish = () => {
    onPublish({
      code,
      name,
      description,
      // El contrato distingue las dos salidas con `asNewVersionOfCode` (§8.4): con código existente,
      // solo K38 publica sobre él; K39 obliga a teclear otro código. S44: con el código bloqueado no
      // hay más salida que la versión n+1 de esa misma operación (RN-01.24).
      asNewVersionOfCode: lockedCode || (codeExists && choice === 'NEW_VERSION' ? code : null)
    })
  }

  return (
    <SmartDialog
      setOpen={open}
      title={{ render: <Typography variant='h6'>{t_(lockedCode ? 'Propose to library' : 'Publish to library')}</Typography> }}
      acceptLabel={t_('Publish')}
      acceptCallback={publish}
      // Solo el ACEPTAR: `disableButtons` apaga también el cancelar, y de una publicación a medias
      // hay que poder salir.
      disableAccept={!code || (!lockedCode && codeExists && !choice)}
      cancelCallback={onClose}
      autoClose={false}
      renderComponent={
        <Stack spacing={2} sx={{ mt: 1, minWidth: 360 }} data-testid='publish-operation-dialog'>
          {lockedCode
            ? (
              <Alert severity='info' data-testid='publish-operation-announcement'>
                {t_('This will create version {{version}} of {{code}}', {
                  version: (preview.currentVersionNumber || 0) + 1,
                  code: lockedCode
                })}
              </Alert>
              )
            : (
              <TextField
                required
                label={t_('Operation code')}
                value={code}
                onChange={(event) => { setCode(event.target.value); setChoice('') }}
              />
              )}
          <TextField label={t_('Operation name')} value={name} onChange={(event) => setName(event.target.value)} />
          <TextField label={t_('Description')} value={description} onChange={(event) => setDescription(event.target.value)} />

          {lockedCode ? <OperationComparisonView comparison={preview.comparison} /> : null}

          {!lockedCode && codeExists
            ? (
              <>
                <Alert severity='info' data-testid='publish-operation-code-exists'>
                  {t_('This code already exists in the library')}
                </Alert>
                <RadioGroup
                  value={choice}
                  onChange={(event) => setChoice(event.target.value)}
                  data-testid='publish-operation-choice'
                >
                  <FormControlLabel
                    value='NEW_VERSION'
                    control={<Radio />}
                    label={t_('Create a new version of this operation') + ' (' + t_('Version') + ' ' + ((preview.currentVersionNumber || 0) + 1) + ')'}
                  />
                  <FormControlLabel
                    value='ANOTHER_CODE'
                    control={<Radio />}
                    label={t_('Create a new operation with another code')}
                  />
                </RadioGroup>
                <OperationComparisonView comparison={preview.comparison} />
              </>
              )
            : null}
        </Stack>
      }
    />
  )
}

export default PublishOperationDialog
